// ════════════════════════════════════════════════════════════════════════════
// LpaKpiCard — card de KPI com label, valor, delta opcional e sparkline
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export interface LpaKpiCardDelta {
  value: number;
  /** Sufixo do delta (default: "%") */
  suffix?: string;
  /** Direção: up (verde), down (vermelho), flat (cinza). Default: auto pelo sinal */
  direction?: "up" | "down" | "flat";
  /** Inverte semântica (down=bom): útil para "drift" ou "custos" */
  invertSemantic?: boolean;
  /** Texto explicativo após o valor (ex: "MoM", "vs CDI") */
  context?: string;
}

export type LpaKpiSize = "sm" | "md" | "lg" | "hero";

export interface LpaKpiCardProps {
  label: string;
  value: string | number;
  delta?: LpaKpiCardDelta;
  /** Sparkline como ReactNode (ex: <LpaSparkline data={...}/>) */
  spark?: React.ReactNode;
  /** Hint/tooltip (ainda sem LpaTooltip — placeholder via title) */
  hint?: string;
  /** Click handler (vira card clickável) */
  onClick?: () => void;
  /** Loading state */
  loading?: boolean;
  size?: LpaKpiSize;
  /** Cor de accent do valor (ex: positive, negative). Default: text */
  valueColor?: string;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
}

const VALUE_FONT: Record<LpaKpiSize, string> = {
  sm: "18px",
  md: "26px",
  lg: "32px",
  hero: "40px",
};

function deltaColors(d: LpaKpiCardDelta): { color: string; bg: string } {
  let dir = d.direction;
  if (!dir) dir = d.value > 0.05 ? "up" : d.value < -0.05 ? "down" : "flat";
  const isGood = d.invertSemantic ? dir === "down" : dir === "up";
  const isBad = d.invertSemantic ? dir === "up" : dir === "down";
  if (isGood) return { color: "var(--lpa-success)", bg: "var(--lpa-success-bg)" };
  if (isBad) return { color: "var(--lpa-danger)", bg: "var(--lpa-danger-bg)" };
  return { color: "var(--lpa-muted)", bg: "var(--lpa-surface)" };
}

export default function LpaKpiCard({
  label,
  value,
  delta,
  spark,
  hint,
  onClick,
  loading = false,
  size = "md",
  valueColor,
  className,
  style,
  testId,
}: LpaKpiCardProps): React.ReactElement {
  if (loading) {
    return (
      <div
        className={className}
        style={{
          background: "var(--lpa-card)",
          border: "1px solid var(--lpa-border)",
          borderRadius: "var(--lpa-radius-lg)",
          padding: "14px 16px",
          minHeight: 110,
          ...style,
        }}
      >
        <div className="lpa-skeleton" style={{ width: 110, height: 9, marginBottom: 10 }} />
        <div className="lpa-skeleton" style={{ width: 80, height: 22, marginBottom: 10 }} />
        <div className="lpa-skeleton" style={{ width: "100%", height: 28 }} />
      </div>
    );
  }

  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={hint}
      data-testid={testId}
      className={className}
      style={{
        background: "var(--lpa-card)",
        border: "1px solid var(--lpa-border)",
        borderRadius: "var(--lpa-radius-lg)",
        padding: "14px 16px",
        cursor: onClick ? "pointer" : undefined,
        transition:
          "transform var(--lpa-tr-fast), box-shadow var(--lpa-tr-fast), border-color var(--lpa-tr-fast)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        outline: "none",
        ...style,
      }}
      onMouseEnter={
        onClick
          ? (e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.borderColor = "var(--lpa-border-strong)";
            }
          : undefined
      }
      onMouseLeave={
        onClick
          ? (e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.borderColor = "var(--lpa-border)";
            }
          : undefined
      }
      onFocus={
        onClick
          ? (e) => {
              e.currentTarget.style.boxShadow = "0 0 0 3px var(--lpa-focus-ring)";
            }
          : undefined
      }
      onBlur={
        onClick
          ? (e) => {
              e.currentTarget.style.boxShadow = "none";
            }
          : undefined
      }
    >
      {/* Label (eyebrow inline — defensivo contra overrides) */}
      <div
        style={{
          fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--lpa-muted)",
          marginBottom: 8,
        }}
      >
        {label}
      </div>

      {/* Value + delta */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: VALUE_FONT[size],
            fontWeight: size === "hero" || size === "lg" ? 700 : 600,
            color: valueColor ?? "var(--lpa-text)",
            letterSpacing: "-0.02em",
            fontFamily:
              size === "lg" || size === "hero" || size === "md"
                ? '"Playfair Display", "Cormorant Garamond", "Garamond", Georgia, serif'
                : 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>
        {delta && (
          <span
            style={{
              fontFamily: "var(--lpa-font-mono)",
              fontSize: "var(--lpa-text-xs)",
              padding: "2px 6px",
              borderRadius: "var(--lpa-radius-sm)",
              ...deltaColors(delta),
              whiteSpace: "nowrap",
            }}
          >
            {delta.value > 0 ? "+" : ""}
            {delta.value.toFixed(1)}
            {delta.suffix ?? "%"}
            {delta.context && <span style={{ marginLeft: 4, opacity: 0.85 }}>{delta.context}</span>}
          </span>
        )}
      </div>

      {/* Sparkline */}
      {spark && <div style={{ height: 28, marginTop: 2 }}>{spark}</div>}
    </div>
  );
}
